import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { MiModal } from "@miplatform/components";
import { BUTTON_LABELS, PERMISSIONS } from "@miplatform/constants";
import { useBoolean, useRouter } from "@miplatform/hooks";
import {
  ButtonVariants,
  CalendarEventType,
  CalendarSetStatus,
  CalendarType,
  Colors,
  IVerticalTabProps,
  PlatformModules,
} from "@miplatform/types";
import { sortByDisplayOrder } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiAlertCircleOutline, mdiChevronDown } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { VerticalTabsLayout } from "../../../../components";
import { useOrderTemplates, usePermissions } from "../../../../hooks";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { ConfigMenu } from "../../components";
import {
  CALENDAR_PAGE_CONFIG,
  MIN_SERVICE_AREA_RADIUS,
  TRAYS_LIMIT,
} from "../ConfigCalendarSetList.constants";
import {
  ConfigCalendarTabs,
  ConfigCalendarUserType,
  ICalendarSetData,
} from "../ConfigCalendarSetList.types";
import {
  getCalendarSetStatusActions,
  getCalendarSetStatusColor,
} from "../ConfigCalendarSetList.util";
import { configCalendarSetSchema } from "../schemas/ConfigCalendarSet.schema";
import { CalendarAttachWorkTemplateBaseView } from "../tabs/CalendarAttachWorkTemplateBaseView/CalendarAttachWorkTemplateBaseView";
import { CalendarDetailsTabView } from "../tabs/CalendarDetailsTabView/CalendarDetailsTabView";
import { CalendarDisplayTypeTabView } from "../tabs/CalendarDisplayTypeTabView/CalendarDisplayTypeTabView";
import { CalendarServiceAreasTabView } from "../tabs/CalendarServiceAreasTabView/CalendarServiceAreasTabView";
import { CalendarTabListView } from "../tabs/CalendarTabListView/CalendarTabListView";
import { CalendarTabView } from "../tabs/CalendarTabView/CalendarTabView";
import { CalendarTrayListTabView } from "../tabs/CalendarTrayListTabView/CalendarTrayListTabView";
import { CalendarTrayTabView } from "../tabs/CalendarTrayTabView/CalendarTrayTabView";
import { CalendarUsersTabView } from "../tabs/CalendarUsersTabView/CalendarUsersTabView";
import { CalendarWorkCapacityListTabView } from "../tabs/CalendarWorkCapacityListTabView/CalendarWorkCapacityListTabView";
import { CalendarWorkCapacityTabView } from "../tabs/CalendarWorkCapacityTabView/CalendarWorkCapacityTabView";
import { CloseoutTabView } from "../tabs/CloseoutTabView/CloseoutTabView";
import {
  canActivateCalendarSet,
  generateCalendarSetDefaultData,
  generateCustomTray,
  generateEmptyCalendarConfig,
} from "./CalendarSetDetails.util";
import { useCalendarSetDetailsViewModel } from "./useCalendarSetDetailsViewModel";

export const ConfigCalendarSetDetails = ({
  module,
  eventType,
}: {
  module: PlatformModules;
  eventType: CalendarEventType;
}) => {
  const [openConfirmModal, handleConfirmModalOpen, handleConfirmModalClose] =
    useBoolean();
  const [addTabModal, handleAddTabModalOpen, handleAddTabModalClose] =
    useBoolean();
  const [addTrayModal, handleAddTrayModalOpen, handleAddTrayModalClose] =
    useBoolean();
  const [selectedTab, setSelectedTab] = useState<string | number | null>(
    ConfigCalendarTabs.DisplayType
  );

  const { params, goBack } = useRouter();
  const { id: calendarSetId } = params;
  const theme = useTheme();

  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission(PERMISSIONS.CALENDAR_SET.UPDATE);

  const form = useForm<ICalendarSetData>({
    mode: "onChange",
    defaultValues: generateCalendarSetDefaultData({ module, eventType }),
    resolver: zodResolver(configCalendarSetSchema),
  });

  const { formState, getValues, setValue, reset, watch } = form;
  const { isDirty, errors } = formState;

  const values = watch();

  const { name, status, workTemplateBases, schedulerUsers } = values;

  const workCapacities = useWatch({
    control: form.control,
    name: "workCapacities",
  });

  const assignableUsers = useWatch({
    control: form.control,
    name: ConfigCalendarUserType.AssignableUsers,
  });

  const calendarConfigs = useWatch({
    control: form.control,
    name: "calendarConfigs",
  });

  const trays = useWatch({
    control: form.control,
    name: "trays",
  });

  const calendarType = getValues("calendarConfigs.0.calendarType");

  const filteredCalendarConfigs = useMemo(
    () =>
      calendarConfigs.filter(
        (config) => config.calendarType === CalendarType.HoldsList
      ),
    [calendarConfigs]
  );

  const serviceAreas = useWatch({
    control: form.control,
    name: "serviceAreas",
  });

  const watchName = useWatch({
    control: form.control,
    name: "name",
  });

  const openOrderLimitPerUser = useWatch({
    control: form.control,
    name: "openOrderLimitPerUser",
  });

  const openAutoOrderLimitPerUser = useWatch({
    control: form.control,
    name: "openAutoOrderLimitPerUser",
  });

  const openOrderLimitWorkBaseTemplateIds = useWatch({
    control: form.control,
    name: "openOrderLimitWorkBaseTemplateIds",
  });

  const { doesServiceAreaExist, doesServiceAreaHasErrors } = useMemo(() => {
    let doesServiceAreaExist = false;
    let doesServiceAreaHasErrors = false;
    const uniqueServiceAreas = new Set<string>();

    serviceAreas.forEach((area) => {
      uniqueServiceAreas.add(`${area.city}-${area.state}`);

      if (area.city && area.state && area.radius) {
        doesServiceAreaExist = true;
      }
      if (
        !area.state ||
        !area.city ||
        !area.radius ||
        parseFloat(area.radius) < MIN_SERVICE_AREA_RADIUS
      ) {
        doesServiceAreaHasErrors = true;
      }
    });

    return {
      doesServiceAreaExist,
      doesServiceAreaHasErrors:
        doesServiceAreaHasErrors ||
        uniqueServiceAreas.size !== serviceAreas.length,
    };
  }, [serviceAreas]);

  const {
    isLoading,
    calendarSetDetails,
    getCalendarSetDetails,
    updateCalendarSet,
    updateCalendarSetStatus,
    createCalendarSet,
  } = useCalendarSetDetailsViewModel({
    module,
    eventType,
    reset,
  });

  const { orderTemplates, getOrderTemplates } = useOrderTemplates({
    module: PlatformModules.MiDispatch,
  });

  useEffect(() => {
    if (calendarSetId) {
      getCalendarSetDetails(calendarSetId);
    }
  }, [calendarSetId]);

  useEffect(() => {
    if (module === PlatformModules.MiDispatch) {
      getOrderTemplates();
    }
  }, [module]);

  const handleAddCalendarConfig = () => {
    const newCalendarConfigs = [...calendarConfigs];
    const newEmptyCalendarConfig = generateEmptyCalendarConfig({
      length: calendarConfigs.length,
    });
    newCalendarConfigs.push(newEmptyCalendarConfig);
    setValue("calendarConfigs", newCalendarConfigs, { shouldDirty: true });
    setSelectedTab(newEmptyCalendarConfig.id);
  };

  const handleAddTray = () => {
    const newTrays = [...trays];
    const customTray = generateCustomTray(newTrays.length);
    newTrays.push(customTray);
    setValue("trays", newTrays, { shouldDirty: true });
    setSelectedTab(customTray.id);
  };

  const handleNavigateToAssignableUsersTab = () =>
    setSelectedTab(ConfigCalendarTabs.AssignableUsers);

  const handleNavigateToWorkTemplateBaseTab = () =>
    setSelectedTab(ConfigCalendarTabs.WorkTemplateBase);

  const handleNextClick = (nextTab: IVerticalTabProps<ICalendarSetData>) => {
    if (
      nextTab.value === ConfigCalendarTabs.ServiceArea &&
      calendarConfigs.length > 1
    ) {
      handleAddTabModalOpen();
      return;
    }

    if (
      nextTab.value === ConfigCalendarTabs.Configs &&
      trays.length > 1 &&
      trays.length < TRAYS_LIMIT
    ) {
      handleAddTrayModalOpen();
      return;
    }

    setSelectedTab(nextTab.value);
  };

  const handleTabModalAddMore = () => {
    handleAddTabModalClose();
    handleAddCalendarConfig();
  };

  const handleTabModalContinue = () => {
    handleAddTabModalClose();
    setSelectedTab(ConfigCalendarTabs.ServiceArea);
  };

  const handleTrayModalAddMore = () => {
    handleAddTrayModalClose();
    handleAddTray();
  };

  const handleTrayModalContinue = () => {
    handleAddTrayModalClose();
    setSelectedTab(ConfigCalendarTabs.Configs);
  };

  const trayTabs = useMemo(
    () =>
      sortByDisplayOrder(trays).map((tray) => ({
        label: tray.name,
        value: tray.id,
        controlledFormViewComponent: CalendarTrayTabView,
        hasErrors: !tray.name,
        isComplete: !!tray.name,
        extraTabProps: {
          id: tray.id,
          isReadOnly,
        },
      })),
    [trays, isReadOnly]
  );

  const userTabs = useMemo(
    () =>
      [...assignableUsers]
        .sort((a, b) => {
          if (a.name && b.name) {
            return a.name.localeCompare(b.name);
          }

          return 0;
        })
        .map((user) => ({
          label: user.name,
          value: user.id,
          controlledFormViewComponent: CalendarWorkCapacityTabView,
          isComplete: Object.values(workCapacities?.[user.id] || {}).some(
            (c) => c.amount > 0
          ),
          extraTabProps: {
            id: user.id,
            isReadOnly,
            eventType,
            orderTemplates,
            onAddWorkTemplateBase: handleNavigateToWorkTemplateBaseTab,
          },
        })) as IVerticalTabProps<ICalendarSetData>[],
    [assignableUsers, isReadOnly, workCapacities]
  );

  const calendarTabs = useMemo(
    () =>
      sortByDisplayOrder(filteredCalendarConfigs).map((config) => {
        const hasErrors =
          !config.attachedTray ||
          !config.followUpDisplay ||
          !config.followUpDefaultOffsetHours ||
          !config.followUpMaxOffsetHours ||
          !config.name;

        return {
          label: config.name || "New Calendar Tab",
          value: config.id,
          controlledFormViewComponent: CalendarTabView,
          isComplete: !hasErrors,
          hasErrors,
          extraTabProps: {
            id: config.id,
            isReadOnly,
          },
        };
      }),
    [filteredCalendarConfigs, isReadOnly]
  );

  const tabs = useMemo<IVerticalTabProps<ICalendarSetData>[]>(() => {
    const tabs = [
      {
        label: "Calendar Display Type",
        value: ConfigCalendarTabs.DisplayType,
        controlledFormViewComponent: CalendarDisplayTypeTabView,
        isComplete: true,
        extraTabProps: {
          eventType,
          isReadOnly,
          isDisabled:
            module === PlatformModules.MiDispatch ||
            (module === PlatformModules.MiScheduling &&
              eventType === CalendarEventType.Order),
        },
      },
      {
        label: "Calendar Details",
        value: ConfigCalendarTabs.Details,
        controlledFormViewComponent: CalendarDetailsTabView,
        hasErrors:
          Boolean(errors.name) || !!errors.dayEndTime || !!errors.dayStartTime,
        isComplete:
          name?.length > 0 && !errors.dayEndTime && !errors.dayStartTime,
        extraTabProps: { isReadOnly, eventType },
      },
      {
        label: `Attach ${CALENDAR_PAGE_CONFIG[eventType].entityNamePlural}`,
        value: ConfigCalendarTabs.WorkTemplateBase,
        controlledFormViewComponent: CalendarAttachWorkTemplateBaseView,
        isComplete: workTemplateBases?.length > 0,
        extraTabProps: { isReadOnly, module, eventType },
      },
      {
        label: "Calendar Users",
        value: ConfigCalendarTabs.CalendarUsers,
        controlledFormViewComponent: CalendarUsersTabView,
        isComplete: schedulerUsers?.length > 0,
        extraTabProps: {
          isReadOnly,
          module,
          eventType,
          onAddWorkTemplateBase: handleNavigateToWorkTemplateBaseTab,
          usersType: ConfigCalendarUserType.SchedulerUsers,
        },
      },
      {
        label: "Assignable Users",
        value: ConfigCalendarTabs.AssignableUsers,
        controlledFormViewComponent: CalendarUsersTabView,
        isComplete: assignableUsers?.length > 0,
        calendarType: [CalendarType.Scheduler, CalendarType.PriorityScheduler],
        extraTabProps: {
          isReadOnly,
          module,
          eventType,
          onAddWorkTemplateBase: handleNavigateToWorkTemplateBaseTab,
          usersType: ConfigCalendarUserType.AssignableUsers,
        },
      },
      {
        label: "Work Capacity",
        value: ConfigCalendarTabs.WorkCapacity,
        controlledFormViewComponent: CalendarWorkCapacityListTabView,
        isComplete: false,
        eventType: CalendarEventType.Order,
        subTabs: userTabs,
        calendarType: [CalendarType.Scheduler],
        extraTabProps: {
          isReadOnly,
          onTabChange: setSelectedTab,
          onAddUser: handleNavigateToAssignableUsersTab,
        },
      },
      ...(module === PlatformModules.MiDispatch
        ? [
            {
              label: "Closeout",
              value: ConfigCalendarTabs.Closeout,
              controlledFormViewComponent: CloseoutTabView,
              extraTabProps: { isReadOnly },
              isComplete:
                ((openOrderLimitPerUser != null && openOrderLimitPerUser > 0) ||
                  (openAutoOrderLimitPerUser != null &&
                    openAutoOrderLimitPerUser > 0)) &&
                openOrderLimitWorkBaseTemplateIds?.length > 0,
            },
          ]
        : []),
      {
        label: "Trays",
        value: ConfigCalendarTabs.Trays,
        controlledFormViewComponent: CalendarTrayListTabView,
        subTabs: trayTabs,
        extraTabProps: {
          isReadOnly,
          onAddTray: handleAddTray,
          onTabChange: setSelectedTab,
        },
      },
      {
        label: "Calendar Tabs",
        value: ConfigCalendarTabs.Configs,
        controlledFormViewComponent: CalendarTabListView,
        subTabs: calendarTabs,
        extraTabProps: {
          isReadOnly,
          onTabChange: setSelectedTab,
          onAddTab: handleAddCalendarConfig,
        },
      },
      {
        label: "Service Area",
        value: ConfigCalendarTabs.ServiceArea,
        isComplete: doesServiceAreaExist && !doesServiceAreaHasErrors,
        hasErrors: doesServiceAreaHasErrors,
        controlledFormViewComponent: CalendarServiceAreasTabView,
        extraTabProps: { isReadOnly },
      },
    ];

    return tabs.filter(
      (tab) =>
        (!tab.calendarType || tab.calendarType.includes(calendarType)) &&
        (!tab.eventType || tab.eventType === eventType)
    );
  }, [
    isReadOnly,
    module,
    trayTabs,
    calendarTabs,
    userTabs,
    errors.name,
    errors.dayEndTime,
    errors.dayStartTime,
    doesServiceAreaExist,
    doesServiceAreaHasErrors,
    workTemplateBases,
    calendarType,
    schedulerUsers,
    assignableUsers,
    openOrderLimitPerUser,
    openAutoOrderLimitPerUser,
    openOrderLimitWorkBaseTemplateIds,
  ]);

  const isValid = useMemo(() => {
    const hasNoErrors = Object.keys(errors).length === 0;
    const hasRequiredFields = watchName.length > 0;

    const configsHaveNoErrors = calendarConfigs
      .slice(eventType === CalendarEventType.WorkOrder ? 2 : 1)
      .every(
        (config) =>
          config.attachedTray &&
          config.followUpDisplay &&
          config.followUpDefaultOffsetHours &&
          config.followUpMaxOffsetHours &&
          config.name
      );

    const traysHaveNoErrors = trays.every((tray) => tray.name);

    const serviceAreasHaveNoErrors = serviceAreas.every(
      (area) =>
        area.city &&
        area.state &&
        area.radius &&
        parseFloat(area.radius) >= MIN_SERVICE_AREA_RADIUS
    );

    return (
      hasNoErrors &&
      hasRequiredFields &&
      configsHaveNoErrors &&
      serviceAreasHaveNoErrors &&
      traysHaveNoErrors
    );
  }, [
    formState,
    trays,
    watchName,
    eventType,
    calendarType,
    serviceAreas,
    calendarConfigs,
  ]);

  const calendarSetActions = {
    cancel: {
      label: BUTTON_LABELS.CANCEL,
      onClick: goBack,
    },
    submit: {
      label: `Save ${CALENDAR_PAGE_CONFIG[eventType].calendarName} Calendar`,
      onClick: handleConfirmModalOpen,
      disabled: !isDirty || isLoading || !isValid,
    },
  };

  const handleSubmitCalendarSet = (isActive: boolean) => {
    const data = {
      ...getValues(),
      status: isActive ? CalendarSetStatus.Active : CalendarSetStatus.Draft,
    };
    if (calendarSetId) {
      updateCalendarSet(data);
    } else {
      createCalendarSet(data);
    }
    handleConfirmModalClose();
  };

  const renderPageTitle = () => {
    const action = calendarSetId ? "Editing" : "Creating";
    const calendarSetLabel = watchName ?? "";
    const title = `${action} ${
      CALENDAR_PAGE_CONFIG[eventType].calendarName
    } Calendar${calendarSetLabel ? ": " : ""}`;
    return (
      <Typography variant="HD4">
        {title}
        <Typography variant="HD4" color="primary" component="span">
          {calendarSetLabel}
        </Typography>
      </Typography>
    );
  };

  return (
    <ConfigPageLayout
      renderTitle={renderPageTitle}
      bottomActions={calendarSetActions}
      loading={isLoading}
      titleActionComponent={
        calendarSetId ? (
          <ConfigMenu
            disabled={isDirty}
            data={getCalendarSetStatusActions(
              calendarSetDetails || values,
              updateCalendarSetStatus
            )}
            optionalElement={{
              size: "medium",
              title: status,
              color: getCalendarSetStatusColor(status),
              endIcon: { path: mdiChevronDown },
              sx: { width: 85 },
            }}
          />
        ) : undefined
      }
    >
      <VerticalTabsLayout
        transparentBackground
        tabs={tabs}
        value={selectedTab}
        form={form}
        setValue={setSelectedTab}
        onNextClick={handleNextClick}
      />
      <MiModal
        title="Tab Added"
        width={448}
        description="You are adding your tab. You can add more or continue with order calendar setup."
        titleIcon={mdiAlertCircleOutline}
        leftIconColor={theme.palette.primary.main}
        open={addTabModal}
        closeModal={handleAddTabModalClose}
        actions={[
          {
            name: "Add More Tabs",
            color: Colors.Primary,
            variant: ButtonVariants.Outlined,
            onClick: handleTabModalAddMore,
          },
          {
            name: "Continue",
            color: Colors.Primary,
            variant: ButtonVariants.Contained,
            onClick: handleTabModalContinue,
          },
        ]}
      />
      <MiModal
        title="Tray Added"
        width={448}
        description="You are adding your tray. You can add more or continue with order calendar setup."
        titleIcon={mdiAlertCircleOutline}
        leftIconColor={theme.palette.primary.main}
        open={addTrayModal}
        closeModal={handleAddTrayModalClose}
        actions={[
          {
            name: "Add More Trays",
            color: Colors.Primary,
            variant: ButtonVariants.Outlined,
            onClick: handleTrayModalAddMore,
          },
          {
            name: "Continue",
            color: Colors.Primary,
            variant: ButtonVariants.Contained,
            onClick: handleTrayModalContinue,
          },
        ]}
      />
      <MiModal
        title="Save Calendar"
        width={448}
        description={
          <>
            You are saving{" "}
            <span
              style={{
                fontWeight: theme.fontWeight.bold,
              }}
            >
              {name}
            </span>
            . Once saved and activated this calendar will be available across
            your org.
            {!canActivateCalendarSet(values) && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                mt={2}
                gap={1}
              >
                <Typography fontSize={14} color={theme.palette.grey.medium}>
                  You must complete the required fields to activate this
                  calendar:
                </Typography>
                {workTemplateBases.length === 0 && (
                  <Typography
                    color={theme.palette.common.black}
                    fontSize={theme.fontSize.medium}
                    fontWeight={theme.fontWeight.semiBold}
                  >
                    {`${CALENDAR_PAGE_CONFIG[eventType].calendarName} Types`}
                  </Typography>
                )}
                {schedulerUsers.length === 0 && (
                  <Typography
                    color={theme.palette.common.black}
                    fontSize={theme.fontSize.medium}
                    fontWeight={theme.fontWeight.semiBold}
                  >
                    Calendar Users
                  </Typography>
                )}
                {assignableUsers.length === 0 &&
                  calendarType === CalendarType.Scheduler && (
                    <Typography
                      color={theme.palette.common.black}
                      fontSize={theme.fontSize.medium}
                      fontWeight={theme.fontWeight.semiBold}
                    >
                      Assignable Users
                    </Typography>
                  )}
              </Box>
            )}
          </>
        }
        titleIcon={mdiAlertCircleOutline}
        leftIconColor={theme.palette.primary.main}
        open={openConfirmModal}
        closeModal={handleConfirmModalClose}
        actions={[
          {
            name: "Save As Draft",
            color: Colors.Orange,
            variant: ButtonVariants.Contained,
            onClick: () => handleSubmitCalendarSet(false),
          },
          {
            name:
              status === CalendarSetStatus.Active
                ? "Save Calendar"
                : "Activate Calendar",
            color: Colors.Success,
            variant: ButtonVariants.Contained,
            disabled: !canActivateCalendarSet(values),
            onClick: () => handleSubmitCalendarSet(true),
          },
        ]}
      />
    </ConfigPageLayout>
  );
};
